/**
 * An assistant message's parts, from Neural's chat/components/AssistantParts.tsx. Tool calls
 * (specialists included, as `bot_portal-*`) collapse into one closed disclosure per run of
 * steps, labelled in plain words; reasoning collapses the same way; the bot's answer is its
 * `deliver` call, rendered as the delivered report. Portal tools are read-only, so no call
 * awaits approval.
 */
import type { ReactNode } from "react";
import { Check, Loader2, X } from "lucide-react";
import { MessagePrimitive, useAuiState } from "@myela/ai-core-react/primitives";
import { StreamingDeliveredReport } from "./DeliveredReport";
import { Disclosure } from "./Disclosure";
import { MessageMarkdown } from "./MessageMarkdown";
import { toolLabel } from "./toolLabels";

type GroupedProps = Parameters<typeof MessagePrimitive.GroupedParts>[0];
type RenderInfo = Parameters<GroupedProps["children"]>[0];
type Part = Parameters<GroupedProps["groupBy"]>[0];
type ToolCall = Extract<RenderInfo["part"], { type: "tool-call" }>;

const groupBy: GroupedProps["groupBy"] = (part: Part) => {
  if (part.type === "reasoning") return ["group-reasoning"];
  if (part.type !== "tool-call" || part.toolName === "deliver") return null;
  return ["group-tools"];
};

// Module-scoped and reference-stable: an inline selector re-renders in a loop.
const selectParts = (s: { message: { parts: readonly Part[] } }) => s.message.parts;

/** One step: its own words; several: how many, plus the one still running. */
function stepsLabel(parts: readonly Part[], indices: readonly number[], running: boolean) {
  const calls = indices.flatMap((i) => {
    const part = parts[i];
    return part?.type === "tool-call" ? [part] : [];
  });
  if (calls.length === 1) return toolLabel(calls[0].toolName, running);
  const current = calls[calls.length - 1];
  if (running) return `${toolLabel(current.toolName, true)} · step ${calls.length}`;
  return `${running ? "Working through" : "Took"} ${calls.length} steps`;
}

type GroupProps = { running: boolean; children: ReactNode };

function Steps({ label, running, children }: GroupProps & { label: string }) {
  return (
    <Disclosure
      label={
        <>
          {running && <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />}
          <span>{label}</span>
        </>
      }
    >
      <div className="flex flex-col gap-1">{children}</div>
    </Disclosure>
  );
}

function ToolsGroup({ indices, running, children }: GroupProps & { indices: readonly number[] }) {
  const parts = useAuiState(selectParts);
  // Running only while the group holds the message's latest part (see ToolStep).
  const live = running && indices.includes(parts.length - 1);
  return (
    <Steps label={stepsLabel(parts, indices, live)} running={live}>
      {children}
    </Steps>
  );
}

/**
 * The host never marks a specialist call (`bot_portal-*`) done in the UI stream, so a call
 * counts as running only while nothing came after it; a later part means it has finished.
 */
function ToolStep({ part }: { part: ToolCall }) {
  const parts = useAuiState(selectParts);
  const last = parts[parts.length - 1];
  const isLatest = last?.type === "tool-call" && last.toolCallId === part.toolCallId;
  const running = part.status.type === "running" && isLatest;
  const failed = part.isError === true;
  return (
    <div className="flex items-center gap-2 py-0.5 text-xs text-muted-foreground">
      {running ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      ) : failed ? (
        <X className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      ) : (
        <Check className="h-4 w-4 shrink-0" aria-hidden />
      )}
      <span>{toolLabel(part.toolName, running)}</span>
      {failed && <span className="sr-only">(failed)</span>}
    </div>
  );
}

function renderPart({ part, children }: RenderInfo) {
  switch (part.type) {
    case "group-tools":
      return (
        <ToolsGroup indices={part.indices} running={part.status.type === "running"}>
          {children}
        </ToolsGroup>
      );
    case "group-reasoning":
      return (
        <Steps label="Reasoning" running={part.status.type === "running"}>
          {children}
        </Steps>
      );
    case "text":
      return <MessageMarkdown />;
    case "reasoning":
      return <p className="whitespace-pre-wrap text-xs text-muted-foreground">{part.text}</p>;
    case "tool-call":
      // Until its input streams in, a delivery has nothing to show.
      if (part.toolName === "deliver") {
        return Object.keys(part.args).length > 0 ? (
          <StreamingDeliveredReport value={part.args} running={part.status.type === "running"} />
        ) : (
          <></>
        );
      }
      return <ToolStep part={part} />;
    default:
      return null;
  }
}

export function AssistantParts() {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <MessagePrimitive.GroupedParts groupBy={groupBy} indicator="never">
        {renderPart}
      </MessagePrimitive.GroupedParts>
    </div>
  );
}
